import { useMemo } from 'react';

// ─── Types ───────────────────────────────────────

export interface QuoteRequest {
  id: string;
  categories: string[];
  status: string;
  created_at: string;
  quotes_count?: number;
  user_data?: Record<string, any>;
  responses_count?: { total_contacted?: number; [key: string]: any };
}

export interface HuntMessage {
  provider_name: string | null;
  provider_id: string | null;
  quote_request_id: string | null;
  message_type: string;
  direction: string;
  status: string;
  subject: string;
  received_at: string;
}

export interface ProviderStatus {
  provider_name: string;
  has_inbound: boolean;
  has_quote: boolean;
  has_unread: boolean;
  status_label: string;
  latest_subject?: string;
}

export interface HuntView {
  quoteRequest: QuoteRequest;
  providers: ProviderStatus[];
  totalProviders: number;
  respondedProviders: number;
  unreadCount: number;
}

export interface HuntStats {
  activeHunts: number;
  providersContacted: number;
  responsesReceived: number;
  quotesReceived: number;
}

// ─── Hook ────────────────────────────────────────

/**
 * @param requests - User's quote requests
 * @param huntMessages - Inbox messages linked to hunts
 * @param offeredProviders - Optional: providers with visible offers per category (public + personalized).
 *   These count as "responded" in hunt stats since we display their prices.
 */
export function useHuntStats(
  requests: QuoteRequest[],
  huntMessages: HuntMessage[],
  offeredProviders?: Record<string, Set<string>>,
) {
  const hunts = useMemo(() => {
    const messagesByHunt = new Map<string, HuntMessage[]>();
    for (const msg of huntMessages) {
      if (msg.quote_request_id) {
        const existing = messagesByHunt.get(msg.quote_request_id) || [];
        existing.push(msg);
        messagesByHunt.set(msg.quote_request_id, existing);
      }
    }

    const huntViews: HuntView[] = requests.map(qr => {
      const msgs = messagesByHunt.get(qr.id) || [];
      const providerMap = new Map<string, HuntMessage[]>();

      for (const msg of msgs) {
        if (msg.provider_name) {
          const existing = providerMap.get(msg.provider_name) || [];
          existing.push(msg);
          providerMap.set(msg.provider_name, existing);
        }
      }

      const providers: ProviderStatus[] = Array.from(providerMap.entries()).map(([name, pMsgs]) => {
        const hasInbound = pMsgs.some(m => m.direction === 'inbound');
        const hasQuote = pMsgs.some(m => m.message_type === 'quote' && m.direction === 'inbound');
        const hasUnread = pMsgs.some(m => m.status === 'unread');
        const inboundMsg = pMsgs.find(m => m.direction === 'inbound');

        let statusLabel = 'Afventer svar';
        if (hasQuote) statusLabel = 'Tilbud modtaget';
        else if (hasInbound) statusLabel = 'Svar modtaget';

        return {
          provider_name: name,
          has_inbound: hasInbound,
          has_quote: hasQuote,
          has_unread: hasUnread,
          status_label: statusLabel,
          latest_subject: inboundMsg?.subject,
        };
      });

      providers.sort((a, b) => {
        if (a.has_unread !== b.has_unread) return a.has_unread ? -1 : 1;
        if (a.has_inbound !== b.has_inbound) return a.has_inbound ? -1 : 1;
        return a.provider_name.localeCompare(b.provider_name);
      });

      // Count responded: providers with inbound messages
      const respondedFromMessages = new Set(
        providers.filter(p => p.has_inbound).map(p => p.provider_name.toLowerCase())
      );

      // Market providers: those with visible prices for this hunt's categories
      const offeredForHunt = new Set<string>();
      if (offeredProviders) {
        for (const cat of qr.categories) {
          const catProviders = offeredProviders[cat];
          if (catProviders) {
            for (const name of catProviders) offeredForHunt.add(name.toLowerCase());
          }
        }
      }

      // emailsSent = providers we actually emailed
      const emailsSent = qr.responses_count?.total_contacted || providers.length;
      // marketProviderCount = providers with market prices (not emailed)
      const marketProviderCount = offeredForHunt.size;
      // totalProviders = emails sent + market providers
      const totalProviders = emailsSent + marketProviderCount;
      // respondedProviders = inbound email replies + market providers (both count as "data received")
      const respondedProviders = new Set([...respondedFromMessages, ...offeredForHunt]).size;

      return {
        quoteRequest: qr,
        providers,
        totalProviders,
        respondedProviders,
        unreadCount: msgs.filter(m => m.status === 'unread').length,
      };
    });

    const activeViews = huntViews.filter(h =>
      ['pending', 'pending_approval', 'sent'].includes(h.quoteRequest.status)
    );

    activeViews.sort((a, b) =>
      new Date(b.quoteRequest.created_at).getTime() - new Date(a.quoteRequest.created_at).getTime()
    );

    // Dedup: if multiple hunts have the same category set, keep the one with most responses
    const seen = new Map<string, HuntView>();
    for (const hunt of activeViews) {
      const key = [...hunt.quoteRequest.categories].sort().join(',');
      const existing = seen.get(key);
      if (!existing || hunt.respondedProviders > existing.respondedProviders) {
        seen.set(key, hunt);
      }
    }
    return Array.from(seen.values());
  }, [huntMessages, requests, offeredProviders]);

  const huntStats: HuntStats = useMemo(() => ({
    activeHunts: hunts.length,
    providersContacted: hunts.reduce((sum, h) => sum + h.totalProviders, 0),
    responsesReceived: hunts.reduce((sum, h) => sum + h.respondedProviders, 0),
    quotesReceived: hunts.reduce((sum, h) =>
      sum + h.providers.filter(p => p.has_quote).length, 0
    ),
  }), [hunts]);

  return { hunts, huntStats };
}
